import { useLazyQuery } from '@apollo/client'
import { useSearchParams } from 'react-router-dom'
import { GET_CUSTOMER_DOCUMENTS } from '../../../utils/GraphQLAPI'
import { Col, Container, Row } from 'react-bootstrap'
import { KTCard } from '../../../_metronic/helpers'
import PdfViewer from '../../sharedComponent/viewer/PDFViewer'
import { AES, enc } from 'crypto-js'
import { useEffect, useState } from 'react'
import { renderFileHeader } from '../../../utils/Contract/ContractUtils'
import SkeletonLoader from '../../sharedComponent/Skeleton'
import { GLOBAL_CONSTANTS } from '../../../utils/GlobalConstants'
import JSZip from 'jszip'

const IMAGE_BASE_URL = (process.env.REACT_APP_IMAGE_BASE_URL ?? 'http://localhost:3000/').replace(/\/$/, '')

const resolveFileUrl = (fileUrl: string): string => {
  try {
    const parsed = new URL(fileUrl)
    return `${IMAGE_BASE_URL}${parsed.pathname}`
  } catch {
    return `${IMAGE_BASE_URL}/${fileUrl.replace(/^\//, '')}`
  }
}

const EnrollmentDocuments = () => {
  const [queryParams] = useSearchParams()
  const [documents, setDocuments] = useState<any>([])
  const [isDownloading, setIsDownloading] = useState(false)

  const [getDocuments, { loading: documentsLoading }] = useLazyQuery(GET_CUSTOMER_DOCUMENTS, {
    onCompleted: (data) => setDocuments(data),
  })

  useEffect(() => {
    const urlParams: any = queryParams.get('pincode')
    const decryptedData = JSON.parse(
      AES.decrypt(urlParams.replaceAll(' ', '+'), 'gDdoxYdfT5XCJw0y').toString(enc.Utf8)
    )
    getDocuments({
      variables: {
        pincode: decryptedData?.pincode,
        tpvId: decryptedData?.tpvId,
        clientId: decryptedData?.clientId,
      },
    })
  }, [])

  const handleDownloadFiles = async () => {
    const files = documents?.getCustomerDocuments
    if (!files?.length) return

    setIsDownloading(true)

    try {
      const zip = new JSZip()

      // Fetch all files in parallel as blobs and add to ZIP
      await Promise.all(
        files.map(async (file: any, index: number) => {
          const fileName = `${renderFileHeader(file?.position).replaceAll(' ', '-')}-${index + 1}.pdf`
          const resolvedUrl = resolveFileUrl(file?.fileUrl)
          const response = await fetch(resolvedUrl)
          if (!response.ok) throw new Error(`Failed to fetch: ${resolvedUrl}`)
          const blob = await response.blob()
          zip.file(fileName, blob)
        })
      )

      // Generate ZIP blob and trigger a single download
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const zipUrl = URL.createObjectURL(zipBlob)

      const anchor = document.createElement('a')
      anchor.href = zipUrl
      anchor.download = 'enrollment-documents.zip'
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      setTimeout(() => URL.revokeObjectURL(zipUrl), 5000)
    } catch (error) {
      console.error('Download failed:', error)
      alert('Some files could not be downloaded. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  if (documentsLoading) {
    return <SkeletonLoader />
  }

  if (documents) {
    return (
      <Container>
        <KTCard className='py-7'>
          <h3 className='text-center'>
            Hi, your enrollment is completed. Please checkout the contract and enrollment related
            files below.
          </h3>
          {documents?.getCustomerDocuments?.length && (
            <div className='text-center'>
              <button
                className='btn btn-primary'
                onClick={handleDownloadFiles}
                disabled={isDownloading}
              >
                {isDownloading ? 'Preparing Download...' : 'Download All Files'}
              </button>
            </div>
          )}
          <Container>
            <Row>
              {documents?.getCustomerDocuments?.length &&
                documents?.getCustomerDocuments
                  ?.filter((file: any) => file.position === 3)
                  ?.map((url: any) => (
                    <Col lg={6} key={url?.fileUrl} className='mb-3'>
                      <p className='fs-4 fw-bold my-5'>Contract</p>
                      <PdfViewer
                        url={resolveFileUrl(url?.fileUrl)}
                        previewSettings={GLOBAL_CONSTANTS.previewSettings}
                      />
                    </Col>
                  ))}
              {documents?.getCustomerDocuments?.length ? (
                documents?.getCustomerDocuments
                  ?.filter((files: any) => files.position !== 3)
                  ?.map((url: any) => {
                    return (
                      <Col lg={6} key={url?.fileUrl} className='mb-3'>
                        <p className='fs-4 fw-bold my-5'>{renderFileHeader(url?.position)}</p>
                        <PdfViewer
                          url={resolveFileUrl(url?.fileUrl)}
                          previewSettings={GLOBAL_CONSTANTS.previewSettings}
                        />
                      </Col>
                    )
                  })
              ) : (
                <div className='text-center'>No files found</div>
              )}
            </Row>
          </Container>
        </KTCard>
      </Container>
    )
  }

  return (
    <Container>
      <div className='text-center alert alert-danger mt-5'>
        <h4 className='fs7'> Oops!, Something went wrong!</h4>
      </div>
    </Container>
  )
}

export default EnrollmentDocuments